import { useEffect, useState, type ReactNode } from "react";
import { Mail, MoreVertical, Plus, Shield, Users } from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import AppCard from "../../components/AppCard";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import { useDemo } from "../../providers/DemoProvider";
import type { GroupId } from "../../types/demo";
import { useAuth } from "../../providers/AuthProvider";
import type { AuthRole, AuthUserStatus } from "../../types/auth";
import { formatShortDate } from "../../services/demoSelectors";
import TeamRoleDialog from "./TeamRoleDialog";
import TeamResetPasswordDialog from "./TeamResetPasswordDialog";
import TeamGroupsDialog from "./TeamGroupsDialog";
import TeamInviteDialog from "./TeamInviteDialog";

type TeamUser = {
  id: string;
  email: string;
  name: string;
  role: AuthRole;
  status: AuthUserStatus;
  groupIds: GroupId[];
  createdAt: string;
  lastLoginAt: string;
  providers: {
    google: boolean;
    microsoft: boolean;
    password: boolean;
  };
};

type TeamInvite = {
  id: string;
  email: string;
  role: AuthRole;
  createdAt: string;
  expiresAt: string;
};

type TeamPayload = {
  currentUserId: string;
  users: TeamUser[];
  invites: TeamInvite[];
};

type ActionMenuState = {
  user: TeamUser;
  top: number;
  left: number;
};

type ActionPanel =
  | { type: "role"; user: TeamUser }
  | { type: "reset-password"; user: TeamUser }
  | { type: "groups"; user: TeamUser }
  | null;

const roleBadge = (role: AuthRole) => {
  return role === "admin"
    ? <Badge tone="info" size="sm" label="Admin" />
    : <Badge tone="neutral" size="sm" label="Team Member" />;
};

const statusBadge = (status: AuthUserStatus) => {
  return status === "active"
    ? <Badge tone="success" size="sm" label="Active" />
    : <Badge tone="warning" size="sm" label="Disabled" />;
};

const readJson = async <T,>(response: Response): Promise<T | null> => {
  return await response.json().catch(() => null) as T | null;
};

export default () => {
  const { state: demoState } = useDemo();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<TeamPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notice, setNotice] = useState<{ type: "error" | "success"; text: string } | null>(null);
  const [actionMenu, setActionMenu] = useState<ActionMenuState | null>(null);
  const [actionPanel, setActionPanel] = useState<ActionPanel>(null);
  const [isInviteDialogOpen, setInviteDialogOpen] = useState(false);

  const loadTeamData = async () => {
    setIsLoading(true);
    setNotice(null);

    const response = await fetch("/api/team");
    const result = await readJson<{ error?: string } & TeamPayload>(response);

    if (!response.ok) {
      setNotice({ type: "error", text: result?.error ?? "Unable to load team members." });
      setIsLoading(false);
      return;
    }

    setData(result);
    setIsLoading(false);
  };

  useEffect(() => {
    if (session?.role === "admin") {
      void loadTeamData();
      return;
    }

    setIsLoading(false);
  }, [session?.role]);

  const currentUserId = data?.currentUserId ?? null;
  const admins = data?.users.filter(user => user.role === "admin") ?? [];
  const teamMembers = data?.users.filter(user => user.role === "team") ?? [];
  const hasPendingInvites = Boolean(data?.invites.length);
  const groupsById = Object.fromEntries(demoState.groups.map(group => [group.id, group])) as Record<GroupId, (typeof demoState.groups)[number]>;

  const closeActionPanel = () => {
    setActionPanel(null);
  };

  const handleToggleStatus = async (user: TeamUser) => {
    setActionMenu(null);
    setNotice(null);

    const response = await fetch(`/api/team/users/${user.id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: user.status === "active" ? "disabled" : "active" }),
    });
    const result = await readJson<{ error?: string }>(response);

    if (!response.ok) {
      setNotice({ type: "error", text: result?.error ?? "Unable to update access." });
      return;
    }

    setNotice({ type: "success", text: user.status === "active" ? "User access disabled." : "User access re-enabled." });
    await loadTeamData();
  };

  const handleInviteSubmit = async (values: { email: string; role: AuthRole }) => {
    setNotice(null);

    const response = await fetch("/api/team/invites", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(values),
    });
    const result = await readJson<{ error?: string }>(response);

    if (!response.ok) {
      setNotice({ type: "error", text: result?.error ?? "Unable to create invite." });
      return;
    }

    setInviteDialogOpen(false);
    setNotice({ type: "success", text: "Invite email sent." });
    await loadTeamData();
  };

  const handleRoleSubmit = async (values: { role: AuthRole }) => {
    if (!actionPanel || actionPanel.type !== "role") return;
    setNotice(null);

    const response = await fetch(`/api/team/users/${actionPanel.user.id}/role`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(values),
    });
    const result = await readJson<{ error?: string }>(response);

    if (!response.ok) {
      setNotice({ type: "error", text: result?.error ?? "Unable to update role." });
      return;
    }

    setNotice({ type: "success", text: "Role updated." });
    closeActionPanel();
    await loadTeamData();
  };

  const handleGroupsSubmit = async (values: { groupIds: GroupId[] }) => {
    if (!actionPanel || actionPanel.type !== "groups") return;
    setNotice(null);

    const response = await fetch(`/api/team/users/${actionPanel.user.id}/groups`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(values),
    });
    const result = await readJson<{ error?: string }>(response);

    if (!response.ok) {
      setNotice({ type: "error", text: result?.error ?? "Unable to assign groups." });
      return;
    }

    setNotice({ type: "success", text: "Group assignments updated." });
    closeActionPanel();
    await loadTeamData();
  };

  const handleResetPassword = async () => {
    if (!actionPanel || actionPanel.type !== "reset-password") return;
    setNotice(null);

    const response = await fetch(`/api/team/users/${actionPanel.user.id}/reset-password`, { method: "POST" });
    const result = await readJson<{ error?: string }>(response);

    if (!response.ok) {
      setNotice({ type: "error", text: result?.error ?? "Unable to reset password." });
      return;
    }

    setNotice({ type: "success", text: "Password reset email sent." });
    closeActionPanel();
    await loadTeamData();
  };

  const handleRevokeInvite = async (inviteId: string) => {
    setNotice(null);

    const response = await fetch(`/api/team/invites/${inviteId}`, { method: "DELETE" });
    const result = await readJson<{ error?: string }>(response);

    if (!response.ok) {
      setNotice({ type: "error", text: result?.error ?? "Unable to revoke invite." });
      return;
    }

    setNotice({ type: "success", text: "Invite revoked." });
    await loadTeamData();
  };

  const openActionMenu = (user: TeamUser, target: HTMLButtonElement) => {
    const rect = target.getBoundingClientRect();
    const menuWidth = 224;
    const viewportPadding = 16;
    const left = Math.min(
      Math.max(viewportPadding, rect.right - menuWidth),
      window.innerWidth - menuWidth - viewportPadding,
    );

    setActionMenu({ user, top: rect.bottom + 4, left });
  };

  const openRolePanel = (user: TeamUser) => {
    if (user.id === currentUserId) {
      setActionMenu(null);
      setNotice({ type: "error", text: "You cannot change your own role here." });
      return;
    }

    setActionMenu(null);
    setActionPanel({ type: "role", user });
  };

  if (session?.role !== "admin") {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
        <p className="text-sm font-semibold">Only admins can manage the team.</p>
        <Button variant="outline" className="mt-4 bg-white" onClick={() => navigate("/dashboard")}>
          Return to dashboard
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading team access...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-3">
        <SummaryCard icon={<Shield className="h-5 w-5" />} label="Admins" value={admins.length} tone="indigo" />
        <SummaryCard icon={<Users className="h-5 w-5" />} label="Team Members" value={teamMembers.length} tone="emerald" />
        <SummaryCard icon={<Mail className="h-5 w-5" />} label="Pending Invites" value={data?.invites.length ?? 0} tone="amber" />
      </div>

      {notice && (
        <div className={`rounded-2xl border px-4 py-3 text-sm ${notice.type === "error" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
          {notice.text}
        </div>
      )}

      <div className={`grid gap-6 ${hasPendingInvites ? "xl:grid-cols-[minmax(0,1.2fr)_380px]" : ""}`}>
        <AppCard
          title="Team Members"
          subtitle="Manage access, password resets, and role changes"
          action={(
            <Button size="sm" className="h-9 w-9 p-0" onClick={() => setInviteDialogOpen(true)} aria-label="Invite member">
              <Plus className="h-4 w-4" />
            </Button>
          )}
        >
          <div className="overflow-x-auto overflow-y-visible">
            <table className="w-full min-w-[760px] table-fixed text-sm">
              <colgroup>
                <col className="w-[32%]" />
                <col className="w-[16%]" />
                <col className="w-[10%]" />
                <col className="w-[20%]" />
                <col className="w-[14%]" />
                <col className="w-[8%]" />
              </colgroup>
              <thead className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="pb-3 pr-4">User</th>
                  <th className="pb-3 pr-4">Role</th>
                  <th className="pb-3 pr-4">Access</th>
                  <th className="pb-3 pr-4">Groups</th>
                  <th className="pb-3 pr-4">Joined</th>
                  <th className="pb-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.users.map(user => {
                  const groupNames = user.groupIds
                    .map(groupId => groupsById[groupId]?.name)
                    .filter((groupName): groupName is string => Boolean(groupName));
                  return (
                  <tr key={user.id} className="align-top">
                    <td className="py-4 pr-4">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900">
                          {user.name}
                          {user.id === currentUserId && <span className="ml-2 text-xs font-medium text-slate-400">You</span>}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">{user.email}</p>
                      </div>
                    </td>
                    <td className="py-4 pr-4">{roleBadge(user.role)}</td>
                    <td className="py-4 pr-4">{statusBadge(user.status)}</td>
                    <td className="py-4 pr-4">
                      {groupNames.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {groupNames.map(groupName => (
                            <Badge key={groupName} tone="neutral" size="sm" label={groupName} />
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400">None</span>
                      )}
                    </td>
                    <td className="py-4 pr-4 text-slate-600">{formatShortDate(user.createdAt)}</td>
                    <td className="py-4 text-right">
                      <div className="relative inline-flex">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={event => {
                            const target = event.currentTarget;
                            if (actionMenu?.user.id === user.id) {
                              setActionMenu(null);
                              return;
                            }
                            openActionMenu(user, target);
                          }}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
        </AppCard>

        <div className="space-y-6">
          {hasPendingInvites && (
            <AppCard title="Pending Invites" subtitle="Outstanding team access invites">
              <div className="space-y-3">
                {data?.invites.map(invite => (
                  <div key={invite.id} className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{invite.email}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {roleBadge(invite.role)}
                          <span className="text-xs text-slate-400">Expires {formatShortDate(invite.expiresAt)}</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => void handleRevokeInvite(invite.id)}>
                        Revoke
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </AppCard>
          )}
        </div>
      </div>

      {actionPanel?.type === "role" && (
        <TeamRoleDialog userEmail={actionPanel.user.email} currentRole={actionPanel.user.role} onSubmit={handleRoleSubmit} onClose={closeActionPanel} />
      )}

      {actionPanel?.type === "reset-password" && (
        <TeamResetPasswordDialog userEmail={actionPanel.user.email} onConfirm={handleResetPassword} onClose={closeActionPanel} />
      )}

      {actionPanel?.type === "groups" && (
        <TeamGroupsDialog userEmail={actionPanel.user.email} currentGroupIds={actionPanel.user.groupIds} groups={demoState.groups} onSubmit={handleGroupsSubmit} onClose={closeActionPanel} />
      )}

      {isInviteDialogOpen && (
        <TeamInviteDialog onSubmit={handleInviteSubmit} onClose={() => setInviteDialogOpen(false)} />
      )}

      {actionMenu && createPortal(
        <>
          <button className="fixed inset-0 z-40 cursor-default" aria-label="Close actions menu" onClick={() => setActionMenu(null)} />
          <div
            className="fixed z-50 w-56 rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
            style={{ top: actionMenu.top, left: actionMenu.left }}
          >
            <button type="button" className="flex w-full items-center px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => void handleToggleStatus(actionMenu.user)}>
              {actionMenu.user.status === "active" ? "Disable access" : "Enable access"}
            </button>
            <button type="button" className="flex w-full items-center px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { setActionMenu(null); setActionPanel({ type: "reset-password", user: actionMenu.user }); }}>
              Reset password
            </button>
            <button type="button" className="flex w-full items-center px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => { setActionMenu(null); setActionPanel({ type: "groups", user: actionMenu.user }); }}>
              Assign to Group
            </button>
            {actionMenu.user.id !== currentUserId ? (
              <button type="button" className="flex w-full items-center px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50" onClick={() => openRolePanel(actionMenu.user)}>
                Update role/access permissions
              </button>
            ) : (
              <div className="px-3 py-2 text-left text-sm text-slate-400">
                You cannot change your own role
              </div>
            )}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

const SummaryCard = ({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  tone: "indigo" | "emerald" | "amber";
}) => {
  const toneClassName = tone === "indigo"
    ? "bg-indigo-50 text-indigo-600"
    : tone === "emerald"
      ? "bg-emerald-50 text-emerald-600"
      : "bg-amber-50 text-amber-500";

  return (
    <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${toneClassName}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs font-medium text-slate-400">{label}</p>
      </div>
    </div>
  );
};
